/**
 * The Simulated Context as the UI edits it (docs/ui-design.md §2): the
 * context minus its date (`now: null` means "the real time at run start"),
 * plus the run's seed. Examples override the defaults when picked.
 */
import type { SimContext } from "../../context";
import { defaultContext } from "./index";

export interface ContextSettings {
  context: Omit<SimContext, "now">;
  /** A fixed simulated date in epoch ms, or null for the real time at run start. */
  now: number | null;
  seed: number;
}

export const DEFAULT_SEED = 1;

export type ContextOverrides = Partial<Omit<SimContext, "now">> & { now?: number };

export function defaultSettings(): ContextSettings {
  const { now: _now, ...context } = defaultContext(0);
  return { context, now: null, seed: DEFAULT_SEED };
}

export function settingsForExample(overrides: ContextOverrides = {}): ContextSettings {
  const { now, ...rest } = overrides;
  const base = defaultSettings();
  return {
    context: { ...base.context, ...structuredClone(rest) },
    now: now ?? null,
    seed: base.seed,
  };
}

export function contextForRun(settings: ContextSettings, realNow: number): SimContext {
  return { ...structuredClone(settings.context), now: settings.now ?? realNow };
}

/** True if the settings differ from the defaults (shown as a badge on the Context button). */
export function isModified(settings: ContextSettings): boolean {
  return JSON.stringify(settings) !== JSON.stringify(defaultSettings());
}

const pad = (n: number, width = 2) => String(n).padStart(width, "0");

/** A `datetime-local` value for an epoch ms (the simulation runs in UTC). */
export function toDateTimeInput(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

export function fromDateTimeInput(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  return Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0));
}

/** Parses a JSON text field and validates it; returns the value or an error message. */
export function parseJsonField<T>(
  text: string,
  parse: (value: unknown) => T | null,
): { value: T } | { error: string } {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (error) {
    return { error: `Invalid JSON: ${error instanceof Error ? error.message : String(error)}` };
  }
  const value = parse(json);
  return value === null ? { error: "This doesn't have the expected shape." } : { value };
}
